import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from './session';
import type { Agreement, Content, Home, HouseInfo, Question, Stay } from './types';

export function useStay() {
  const { api, token } = useSession();
  return useQuery({ queryKey: ['stay', token], queryFn: () => api.get<Stay>('/guest/v1/stay'), enabled: !!token });
}

export function useHome() {
  const { api, token } = useSession();
  return useQuery({
    queryKey: ['home', token],
    queryFn: () => api.get<Home>('/guest/v1/home'),
    enabled: !!token,
    refetchInterval: 5 * 60_000, // the door code appears at 9:00 on arrival day
  });
}

export function useHouseInfo(enabled = true) {
  const { publicApi } = useSession();
  return useQuery({
    queryKey: ['house-info'],
    queryFn: () => publicApi.get<HouseInfo>('/guest/v1/house'),
    staleTime: 10 * 60_000,
    enabled,
  });
}

/** The house's name and Text us, from the stay when signed in, else the public house info. */
export function useHouseBasics(): { name: string; textUs: string } | null {
  const { token, resuming } = useSession();
  const stay = useStay();
  const info = useHouseInfo(!token && !resuming);
  const h = stay.data?.house ?? info.data?.house;
  return h ? { name: h.name, textUs: h.textUs } : null;
}

const EMPTY = { title: '', intro: '', sections: [] };
const NO_CONTENT: Content = { stay: EMPTY, house: EMPTY, local: EMPTY, feedback: EMPTY, help: EMPTY };

/** The guest's full guide, or with no stay the public part of it (ADR-041, ADR-045). Tabs a
 *  server from before ADR-050 doesn't send come back empty rather than missing. */
export function useContent() {
  const { api, publicApi, token, resuming } = useSession();
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['content', token],
    queryFn: async (): Promise<Content> => {
      const got = token
        ? await api.get<Partial<Content>>('/guest/v1/content')
        : ((
            await qc.fetchQuery({
              queryKey: ['house-info'],
              queryFn: () => publicApi.get<HouseInfo>('/guest/v1/house'),
              staleTime: 10 * 60_000,
            })
          ).content ?? {});
      return { ...NO_CONTENT, ...got };
    },
    enabled: !!token || !resuming,
    staleTime: 10 * 60_000,
  });
}

export function useAgreement() {
  const { api, token } = useSession();
  return useQuery({ queryKey: ['agreement', token], queryFn: () => api.get<Agreement>('/guest/v1/agreement'), enabled: !!token });
}

export function useQuestions() {
  const { api, token } = useSession();
  return useQuery({
    queryKey: ['questions', token],
    queryFn: () => api.get<{ questions: Question[]; consentLabel: string }>('/guest/v1/questions'),
    enabled: !!token,
  });
}
